import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IChecklistReportData } from '../../checklist-report';
import { AppTextService } from '@fi-shared/app-text';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import { ChecklistService } from '@fi-app/checklist/checklist.service';
import { ChecklistReportHelperService } from '../../checklist-report-helper.service';
import { PublicationType } from '@fi-shared/types/pubication-type';
import { ChartOptions } from 'chart.js';
import { IChartClickEvent } from '@fi-shared/fi-chart/fi-chart';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-checklist-report-over-time-units',
    templateUrl: './checklist-report-over-time-units.component.html',
    styleUrls: ['./checklist-report-over-time-units.component.scss'],
    standalone: false,
})
export class ChecklistReportOverTimeUnitsComponent implements OnInit, OnChanges {
    @Input() reportData: IChecklistReportData;
    @Input() checklist: ChecklistModel;

    chartData: number[][];
    chartSeries: string[];
    chartColors: string[];
    chartLabels: string[];
    chartOptions: ChartOptions<'line'>;

    constructor(
        private appTextService: AppTextService,
        private checklistService: ChecklistService,
        private checklistReportHelperService: ChecklistReportHelperService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes.reportData;
        if (change && !change.firstChange) {
            this.updateChartData();
        }
    }

    ngOnInit() {
        this.updateChartData();

        this.chartOptions = {
            plugins: {
                tooltip: {
                    position: 'nearest',
                    callbacks: {
                        label: (label) => {
                            let total = 0;
                            this.chartData.forEach((data) => {
                                total += data[label.dataIndex];
                            });
                            const value = label.dataset.data[label.dataIndex] as number;
                            return [
                                value + ' (' + ((value / total) * 100).toFixed(1).toString().replace(/\.0$/, '') + '%)',
                            ];
                        },
                    },
                },
                legend: {
                    display: false,
                },
            },
            elements: {
                line: {
                    fill: false,
                },
                point: {
                    radius: 5,
                },
            },
            scales: {
                y: {
                    ticks: {
                        callback: (value: number) => {
                            return Math.abs(Math.floor(value) - value) < 0.001 ? Math.floor(value) : '';
                        },
                    },
                    beginAtZero: true, // minimum value will be 0.
                },
            },
        };
    }

    canSelectStatus(status) {
        return this.checklistReportHelperService.canSelectStatus(status, this.reportData);
    }

    selectStatus(status, $event?) {
        if ($event) {
            $event.preventDefault();
        }
        this.checklistReportHelperService.selectStatus(status, this.reportData, this.checklist.Id);
    }

    updateChartData() {
        this.chartData = [];
        this.chartSeries = [];
        this.chartColors = [];
        this.chartLabels = this.reportData.Periods.map((item) => {
            const startDate = this.fiFormatDateService.formatDate(item.StartDateTime, 'date');
            const endDate =
                this.reportData.Periods.length > 1 &&
                this.checklist.PublicationSetting.PublicationType === PublicationType.Continous
                    ? startDate
                    : this.checklistService.getEndDateText(this.checklist, item.EndDateTime).toLowerCase();

            return startDate === endDate ? startDate : startDate + ' - ' + endDate;
        });

        this.reportData.Statuses.forEach((statusData, i) => {
            this.chartSeries.push(this.appTextService.get(ChecklistModel.StatusTextIds[statusData.Status]));
            this.chartData[i] = statusData.PeriodsData.map(function (item) {
                return item.UnitsCount;
            });
            this.chartColors.push(ChecklistModel.StatusColors[statusData.Status]);
        });
    }

    chartClick($event: IChartClickEvent) {
        if ($event.active.length > 0) {
            const datasetIndex = $event.active[0].datasetIndex;
            this.selectStatus(this.reportData.Statuses[datasetIndex].Status);
        }
    }

    getStatusName(status) {
        return this.appTextService.get(ChecklistModel.StatusTextIds[status]);
    }
}
